import type { DocumentNode, OperationVariables } from '@apollo/client/core'
import type { QueryHookOptions, QueryResult as ApolloQueryResult } from '@apollo/client/react'
import type { TypedDocumentNode } from '@apollo/client'
import type { Dispatch, SetStateAction } from 'react'
import type { FilterQuery, Sort, SortDirection, Template } from '@ztechapps/common.contract'
import type { IStorage } from '@ztechapps/common.utils'
import type { GraphQLFormattedError } from 'graphql'

export type GraphqlQuery = DocumentNode | TypedDocumentNode<any, OperationVariables>
export type Operator =
  | 'equal'
  | 'not_equal'
  | 'less'
  | 'greater'
  | 'greater_or_equal'
  | 'less_or_equal'
  | 'like'
  | 'not_like'
  | 'starts_with'
  | 'ends_with'
  | 'between'
  | 'not_between'
  | 'is_null'
  | 'is_not_null'
  | 'is_empty'
  | 'is_not_empty'
  | 'select_equals'
  | 'select_not_equals'
  | 'select_any_in'
  | 'select_not_any_in'
  | 'multiselect_equals'
  | 'multiselect_not_equals'
  | 'proximity'

export type SortValue = { name: string; direction: SortDirection }
export type Search = Record<string, any>
export enum SearchTypeEnum {
  startsWith = 'startsWith',
  fullText = 'fullText',
}

export type TableFilter<T = any> = Record<string, FilterQuery<T>>
export type OnSortFunction<T = any> = ({
  sortObject,
  sortString,
  sortValue,
  sortFields,
}: {
  sortObject?: { sort: Sort<T> }
  sortString?: string
  sortValue?: SortValue
  sortFields?: ListField[]
}) => { sort: Sort<T> }

export type OnFilterFunction<T = any> = (args: {
  template?: Template
  tableFilter?: TableFilter<T>
  searchObject?: Search
  searchValue?: string
}) => {
  filter: FilterQuery<T>
  filterTemplate: Template
  tableFilter: TableFilter<T>
  searchValue: string
  searchObj: Search
}
export type OnChangeSearchFieldFunction = (args: ListField) => { searchField: ListField }
export type OnChangeSearchTypeFunction = (args: SearchTypeEnum) => { searchType: SearchTypeEnum }
export type OnChangeFilterCascadeFunction = (args: boolean) => { cascadeFilter: boolean }

export enum ListFieldTypeEnum {
  dateTimeZone = 'dateTimeZone',
  date = 'date',
  select = 'select',
  number = 'number',
  boolean = 'boolean',
  text = 'text',
  array = 'array',
  objectArray = 'objectArray',
  positiveCountSelect = 'positiveCountSelect',
}
/** The two choices of a `positiveCountSelect` field — the count is above zero, or not above zero. */
export enum PositiveCountOptionEnum {
  AboveZero = 'AboveZero',
  NotAboveZero = 'NotAboveZero',
}
export type ListField = {
  name: string
  displayName?: string
  type?: ListFieldTypeEnum
  defaultOnSearch?: boolean
  excludeOnSearch?: boolean
  excludeOnSort?: boolean
  excludeOnFilter?: boolean
  excludeOnDisplay?: boolean //the items is included on search or filter, but cannot be shown
  fieldSettings?: {
    listValues?: any
    /** select fields only — the label of an extra header-filter entry that matches a null or missing value */
    emptyValueLabel?: string
  }
  excludeOperators?: Operator[]
  defaultOperator?: Operator
  index?: number
  size?: number
  isVisible?: boolean
  key?: string
}
export type ListFieldConfig = Record<string, ListField>

export enum HydrationStatusEnum {
  hydrating = 'hydrating',
  hydrated = 'hydrated',
}

export type ListAction<T = any> = {
  filter: FilterQuery<T>
  sort: Sort<T>
  searchValue: string
  searchObj: Search
  filterTemplate: Template
  searchField: ListField
  searchType: SearchTypeEnum
  cascadeFilter: boolean
  tableFilter: TableFilter
}

export type ListActionResult<T = any> = ListAction<T> & {
  //This functions should be the one that is used when executing the operation
  onSort: OnSortFunction
  onFilter: OnFilterFunction

  //whether this list offers search UI at all — a presence flag, not a callback (search itself runs
  //through onFilter). Consumers pass `searchable={false}` to suppress the search control.
  searchable: boolean
  onChangeSearchField: OnChangeSearchFieldFunction
  onChangeSearchType: OnChangeSearchTypeFunction
  onChangeFilterCascade: OnChangeFilterCascadeFunction

  //this set values, will just directly set the value of variable, just use this internally
  setFilter: Dispatch<SetStateAction<FilterQuery<T>>>
  setSort: Dispatch<SetStateAction<Sort<T>>>
  setSearchObj: Dispatch<SetStateAction<Search>>
  setSearchValue: Dispatch<SetStateAction<string>>
  setFilterTemplate: Dispatch<SetStateAction<Template>>
  setSearchField: Dispatch<SetStateAction<ListField>>
  setSearchType: Dispatch<SetStateAction<SearchTypeEnum>>
  setCascadeFilter: Dispatch<SetStateAction<boolean>>
  hydrationStatus: HydrationStatusEnum
  storageKey: string
  storage: IStorage<ListAction<T>>
}

export type DbOptions = Record<any, any> | null

export type QueryInput<T = any> = {
  query: GraphqlQuery
  entity?: string
  projection?: Record<string, any>
  sort?: Sort<T>
  limit?: number
  skip?: number
  filter?: FilterQuery<T> | null
  options?: {
    queryOptions?: QueryHookOptions<any, any>
    dbOptions?: DbOptions
  }
}

export type LoadMoreEntriesFunction = (skip: number, searchString: string, sortObject: Record<any, any>) => any
export type PageInfo = {
  count: number
}
export type ListQueryResult<T = any> = {
  isEmpty: boolean
  sort: Sort<T>
  filter: FilterQuery<T>
  loadMoreEntries: LoadMoreEntriesFunction
  pageInfo: PageInfo
  list: T[]
} & ApolloQueryResult<any, any> &
  ListActionResult<T>

export type DocumentQueryProps = {
  _id?: string
} & QueryInput

/** GraphQL errors raised for single fields while the document itself loaded */
export type DocumentFieldErrors = ReadonlyArray<GraphQLFormattedError>

export type DocumentQueryResult<T = any> = {
  isEmpty: boolean
  document: T
  error: any
  /** GraphQL errors raised for single fields while the document itself loaded (see `splitDocumentError`) */
  fieldErrors: DocumentFieldErrors
  loading: boolean
  refetch: any
} & Omit<ListQueryResult<T>, 'list' | 'dataArray' | 'pageInfo' | 'loadMoreEntries'>

export type QueryResult<T = any> = ListQueryResult<T> | DocumentQueryResult<T>
